import React, {
  forwardRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle
} from 'react'
import { useController, useForm } from 'react-hook-form'
import { If, Then, Else } from 'react-if'
import { Grid, GridSize, GridSpacing } from '@material-ui/core'

import {
  UnidadeGestoraServices,
  INewAutoCompleteRelacao,
  NmTipoUnidade,
  IUnidadeGestoraResponse
} from 'services/Utils/UnidadeGestora'
import { useDebounce, usePersistForm } from 'hooks'

import { AutoCompleteSearchTerm } from 'components'
import { Loading } from './types'

type FieldsNames =
  | 'DiretoriaExecutiva'
  | 'Diretoria'
  | 'GerenciaGeral'
  | 'Gerencia'
  | 'Unidade'

enum Fields {
  Diretoria = 'Diretoria',
  DiretoriaExecutiva = 'DiretoriaExecutiva',
  Gerencia = 'Gerencia',
  GerenciaGeral = 'GerenciaGeral'
}

interface IGridCols {
  DiretoriaExecutiva?: GridSize
  Diretoria?: GridSize
  GerenciaGeral?: GridSize
  Gerencia?: GridSize
  Unidade?: GridSize
  spacing?: GridSpacing
}

export interface IOnChangeUnidadeGestora {
  DiretoriaExecutiva: number | null
  Diretoria: number | null
  GerenciaGeral: number | null
  Gerencia: number | null
}

interface UnidadeGestoraFiledsProps {
  disableFields?: FieldsNames[]
  disabled?: boolean
  renderUnidadeSelect?: boolean
  formValue?: any
  control?: any
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: unknown) => void
  gridCols: IGridCols
  defaultValue?: any
  ref?: any
  showInputs?: {
    DiretoriaExecutiva?: boolean
    Diretoria?: boolean
    GerenciaGeral?: boolean
    Gerencia?: boolean
  }
}

interface IUnidadeGestoraOptions {
  DiretoriaExecutiva: INewAutoCompleteRelacao[]
  Diretoria: INewAutoCompleteRelacao[]
  GerenciaGeral: INewAutoCompleteRelacao[]
  Gerencia: INewAutoCompleteRelacao[]
}

const unidade_relacao_PaiFilho = {
  DiretoriaExecutiva: {
    pais: [],
    filhos: ['Diretoria', 'GerenciaGeral', 'Gerencia']
  },
  Diretoria: {
    pais: ['DiretoriaExecutiva'],
    filhos: ['GerenciaGeral', 'Gerencia']
  },
  GerenciaGeral: {
    pais: ['DiretoriaExecutiva', 'Diretoria'],
    filhos: ['Gerencia']
  },
  Gerencia: {
    pais: ['DiretoriaExecutiva', 'Diretoria', 'GerenciaGeral'],
    filhos: []
  }
}

const UnidadeGestoraFields: React.FC<UnidadeGestoraFiledsProps> = forwardRef(
  (
    {
      disableFields,
      renderUnidadeSelect,
      formValue = '',
      control,
      onChange,
      disabled,
      gridCols,
      defaultValue,
      showInputs = {
        Diretoria: true,
        DiretoriaExecutiva: true,
        Gerencia: true,
        GerenciaGeral: true
      }
    },
    ref
  ) => {
    const {
      control: mockControl,
      setValue,
      watch,
      reset
    } = useForm<any>({
      defaultValues: {
        Gerencia: null,
        Diretoria: null,
        GerenciaGeral: null,
        DiretoriaExecutiva: null
      }
    })
    const { field } = useController({
      name: formValue,
      control: control ? control : mockControl // mockControl só para não dar erro caso não
    })

    const unidadeGestoraValues_default = {
      DiretoriaExecutiva: watch('DiretoriaExecutiva') ?? null,
      Diretoria: watch('Diretoria') ?? null,
      GerenciaGeral: watch('GerenciaGeral') ?? null,
      Gerencia: watch('Gerencia') ?? null
    }

    const unidadeGestoraOptions_default = JSON.parse(
      localStorage.getItem('FORM_unidade_gestora_options')
    ) ?? {
      Gerencia: [],
      Diretoria: [],
      GerenciaGeral: [],
      DiretoriaExecutiva: []
    }

    const [unidadeGestoraOptions, setUnidadeGestoraOptions] =
      useState<IUnidadeGestoraOptions>(unidadeGestoraOptions_default)
    const [globalSelectOptions, setGlobalSelectOptions] = useState<
      IUnidadeGestoraResponse[]
    >([])
    const [globalSelectValue, setGlobalSelectValue] =
      useState<IUnidadeGestoraResponse>(null)
    const [unidadeGestoraValues, setUnidadeGestoraValues] = useState(
      unidadeGestoraValues_default
    )
    const [loading, setLoading] = useState<Loading>({
      Gerencia: false,
      Diretoria: false,
      GerenciaGeral: false,
      DiretoriaExecutiva: false,
      select: false
    })

    usePersistForm('unidade-gestora-form', {
      setValue: setValue,
      watch: watch
    })

    useImperativeHandle(ref, () => ({ clearForm }))

    const clearForm = () => {
      localStorage.removeItem('FORM_unidade_gestora_options')
      localStorage.removeItem('FORM_unidade-gestora-form')
      reset()

      setUnidadeGestoraOptions({
        Diretoria: [],
        DiretoriaExecutiva: [],
        Gerencia: [],
        GerenciaGeral: []
      })
      setUnidadeGestoraValues({
        Diretoria: null,
        DiretoriaExecutiva: null,
        Gerencia: null,
        GerenciaGeral: null
      })
      setGlobalSelectOptions(null)
      setGlobalSelectValue(null)
    }

    const handleSearchGlobalSelectUnidade = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setLoading((prevState) => ({
        ...prevState,
        select: true
      }))

      UnidadeGestoraServices()
        .getByName(event.target.value)
        .then((response) => {
          if (response.status === 200) {
            setGlobalSelectOptions(response.data)
          }

          setLoading((prevState) => ({
            ...prevState,
            select: false
          }))
        })
        .catch((error) => {
          console.error(error)
          setLoading((prevState) => ({
            ...prevState,
            select: false
          }))
        })
    }

    const handleChangeSelectUnidadeGlobal = (
      unidadeGlobalSelecionada: IUnidadeGestoraResponse
    ) => {
      setGlobalSelectValue(unidadeGlobalSelecionada)

      const relacoes = {
        ...unidadeGestoraValues_default,
        [unidadeGlobalSelecionada?.nmTipoUnidade]: unidadeGlobalSelecionada
      }

      if (unidadeGlobalSelecionada?.relacoes?.length > 0) {
        for (const relacao of unidadeGlobalSelecionada.relacoes) {
          relacoes[relacao.nmTipoUnidade] = relacao
        }
      }

      setUnidadeGestoraValues(relacoes)
      if (control) {
        field.onChange(unidadeGlobalSelecionada?.idUnidadeGestora || null)
      } else {
        onChange(unidadeGlobalSelecionada?.idUnidadeGestora || null)
      }
    }

    const handleSearchUnidadeGestora = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setLoading((prevState) => ({
        ...prevState,
        [event.target.name]: true
      }))

      UnidadeGestoraServices()
        .newAutoComplete({
          name: event.target.value,
          tpUnidade: event.target.name
        })
        .then((response) => {
          if (response.status === 200) {
            setUnidadeGestoraOptions((prevState) => ({
              ...prevState,
              [event.target.name]: response.data
            }))
          }

          setLoading((prevState) => ({
            ...prevState,
            [event.target.name]: false
          }))
        })
        .catch((error) => {
          console.error(error)
          setLoading((prevState) => ({
            ...prevState,
            [event.target.name]: false
          }))
        })
    }

    const handleChangeUnidadeGestora = (
      unidadeSelecionada: INewAutoCompleteRelacao,
      typeUnidadeSelecionada: string
    ) => {
      if (!unidadeSelecionada) {
        setValue(typeUnidadeSelecionada, null)
        localStorage.setItem(
          'FORM_unidade-gestora-form',
          JSON.stringify(watch())
        )

        setUnidadeGestoraValues({
          DiretoriaExecutiva: watch('DiretoriaExecutiva'),
          Diretoria: watch('Diretoria'),
          Gerencia: watch('Gerencia'),
          GerenciaGeral: watch('GerenciaGeral')
        })
        setUnidadeGestoraOptions(unidadeGestoraOptions_default)
        return
      }

      const unidadePais = {}
      const unidadeFilhosParaLimpar = {}
      const unidadeFilhos = {
        [unidadeSelecionada?.nmTipoUnidade]:
          unidadeGestoraOptions[unidadeSelecionada?.nmTipoUnidade]
      }

      if (unidadeSelecionada?.pais?.length > 0) {
        for (const pai of unidadeSelecionada.pais) {
          if (!unidadePais[pai.nmTipoUnidade]) {
            unidadePais[pai.nmTipoUnidade] = pai
            setValue(pai.nmTipoUnidade, pai)
          }
        }
      }

      if (unidadeSelecionada?.filhos?.length > 0) {
        for (const filho of unidadeSelecionada.filhos) {
          if (
            unidade_relacao_PaiFilho[
              unidadeSelecionada.nmTipoUnidade
            ].filhos.includes(filho.nmTipoUnidade)
          ) {
            unidadeFilhos[filho.nmTipoUnidade] =
              unidadeSelecionada.filhos.filter(
                (unidade) => unidade.nmTipoUnidade === filho.nmTipoUnidade
              )
            unidadePais[filho.nmTipoUnidade] = null
            unidadeFilhosParaLimpar[filho.nmTipoUnidade] = null
          }
        }

        setUnidadeGestoraOptions((prevState) => ({
          ...prevState,
          ...unidadeFilhos
        }))
        localStorage.setItem(
          'FORM_unidade_gestora_options',
          JSON.stringify(unidadeGestoraOptions)
        )
      }
      setValue(unidadeSelecionada.nmTipoUnidade, unidadeSelecionada)
      setUnidadeGestoraValues((prevState) => ({
        ...prevState,
        ...unidadePais,
        [unidadeSelecionada?.nmTipoUnidade]: unidadeSelecionada
      }))
    }

    const getValue = useCallback(
      (fieldName: FieldsNames): string => {
        const unidade = watch(fieldName)

        return (
          unidade?.nmUnidade || unidadeGestoraValues[fieldName]?.nmUnidade || ''
        )
      },
      [unidadeGestoraValues]
    )

    const getGlobalValue = useCallback((): string => {
      return globalSelectValue?.nmUnidade || ''
    }, [globalSelectValue])

    const getDefaultValue = () => {
      setLoading({
        select: true,
        Gerencia: true,
        Diretoria: true,
        GerenciaGeral: true,
        DiretoriaExecutiva: true
      })

      UnidadeGestoraServices()
        .getById(defaultValue)
        .then((response) => {
          if (response.status === 200) {
            handleChangeSelectUnidadeGlobal(response.data)
          }
          setLoading({
            select: false,
            Gerencia: false,
            Diretoria: false,
            GerenciaGeral: false,
            DiretoriaExecutiva: false
          })
        })
        .catch((error) => {
          console.error(error)
          setLoading({
            select: false,
            Gerencia: false,
            Diretoria: false,
            GerenciaGeral: false,
            DiretoriaExecutiva: false
          })
        })
    }

    useEffect(() => {
      if (defaultValue) {
        getDefaultValue()
      }
    }, [defaultValue])

    useEffect(() => {
      if (!renderUnidadeSelect) {
        const unidades = {}

        Object.keys(unidadeGestoraValues).forEach((key) => {
          const unidade = unidadeGestoraValues[key]
          const unidadeWatch = watch(key)
          unidades[key] = unidadeWatch?.idUnidadeGestora ?? null
        })

        if (control) {
          field.onChange(unidades)
        } else {
          onChange(unidades as IOnChangeUnidadeGestora)
        }
      }
    }, [unidadeGestoraValues])

    return (
      <>
        <Grid container spacing={gridCols.spacing}>
          <If condition={control}>
            <Then>
              <If condition={renderUnidadeSelect}>
                <Then>
                  <Grid item md={gridCols['Unidade']}>
                    <AutoCompleteSearchTerm
                      onChangeText={useDebounce(
                        handleSearchGlobalSelectUnidade,
                        500
                      )}
                      disabled={disableFields?.includes('Unidade') || disabled}
                      value={getGlobalValue()}
                      onChange={(_event, value) =>
                        handleChangeSelectUnidadeGlobal(value)
                      }
                      optionLabel="nmUnidade"
                      options={globalSelectOptions}
                      loading={loading.select}
                      label="Unidade Gestora"
                    />
                  </Grid>
                </Then>
              </If>
              <If condition={showInputs.DiretoriaExecutiva}>
                <Then>
                  <Grid item md={gridCols.DiretoriaExecutiva}>
                    <AutoCompleteSearchTerm
                      name={Fields.DiretoriaExecutiva}
                      formValue={Fields.DiretoriaExecutiva}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.DiretoriaExecutiva) ||
                        disabled
                      }
                      value={getValue(Fields.DiretoriaExecutiva)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'DiretoriaExecutiva')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      options={unidadeGestoraOptions[Fields.DiretoriaExecutiva]}
                      optionLabel="nmUnidade"
                      label="Diretoria Executiva"
                      loading={loading[Fields.DiretoriaExecutiva]}
                    />
                  </Grid>
                </Then>
              </If>
              <If condition={showInputs.Diretoria}>
                <Then>
                  <Grid item md={gridCols.Diretoria}>
                    <AutoCompleteSearchTerm
                      name={Fields.Diretoria}
                      formValue={Fields.Diretoria}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.Diretoria) || disabled
                      }
                      value={getValue(Fields.Diretoria)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'Diretoria')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      options={unidadeGestoraOptions.Diretoria}
                      optionLabel="nmUnidade"
                      label="Diretoria"
                      loading={loading[Fields.Diretoria]}
                    />
                  </Grid>
                </Then>
              </If>
              <If condition={showInputs.GerenciaGeral}>
                <Then>
                  <Grid item md={gridCols.GerenciaGeral}>
                    <AutoCompleteSearchTerm
                      name={Fields.GerenciaGeral}
                      formValue={Fields.GerenciaGeral}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.GerenciaGeral) ||
                        disabled
                      }
                      value={getValue(Fields.GerenciaGeral)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'GerenciaGeral')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      options={unidadeGestoraOptions.GerenciaGeral}
                      optionLabel="nmUnidade"
                      label="Gerência Geral"
                      loading={loading[Fields.GerenciaGeral]}
                    />
                  </Grid>
                </Then>
              </If>
              <If condition={showInputs.Gerencia}>
                <Then>
                  <Grid item md={gridCols.Gerencia}>
                    <AutoCompleteSearchTerm
                      name={Fields.Gerencia}
                      formValue={Fields.Gerencia}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.Gerencia) || disabled
                      }
                      value={getValue(Fields.Gerencia)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'Gerencia')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      optionLabel="nmUnidade"
                      label="Gerência"
                      options={unidadeGestoraOptions.Gerencia}
                      loading={loading[Fields.Gerencia]}
                    />
                  </Grid>
                </Then>
              </If>
            </Then>

            <Else>
              <If condition={showInputs.DiretoriaExecutiva}>
                <Then>
                  <Grid item md={gridCols.DiretoriaExecutiva}>
                    <AutoCompleteSearchTerm
                      name={Fields.DiretoriaExecutiva}
                      formValue={Fields.DiretoriaExecutiva}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.DiretoriaExecutiva) ||
                        disabled
                      }
                      value={getValue(Fields.DiretoriaExecutiva)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'DiretoriaExecutiva')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      optionLabel="nmUnidade"
                      label="Diretoria Executiva"
                      options={unidadeGestoraOptions.DiretoriaExecutiva}
                      loading={loading[Fields.DiretoriaExecutiva]}
                    />
                  </Grid>
                </Then>
              </If>
              <If condition={showInputs.Diretoria}>
                <Then>
                  <Grid item md={gridCols.Diretoria}>
                    <AutoCompleteSearchTerm
                      name={Fields.Diretoria}
                      formValue={Fields.Diretoria}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.Diretoria) || disabled
                      }
                      value={getValue(Fields.Diretoria)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'Diretoria')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      optionLabel="nmUnidade"
                      label="Diretoria"
                      options={unidadeGestoraOptions.Diretoria}
                      loading={loading[Fields.Diretoria]}
                    />
                  </Grid>
                </Then>
              </If>
              <If condition={showInputs.GerenciaGeral}>
                <Then>
                  <Grid item md={gridCols.GerenciaGeral}>
                    <AutoCompleteSearchTerm
                      name={Fields.GerenciaGeral}
                      formValue={Fields.GerenciaGeral}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.GerenciaGeral) ||
                        disabled
                      }
                      value={getValue(Fields.GerenciaGeral)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'GerenciaGeral')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      optionLabel="nmUnidade"
                      label="Gerencia Geral"
                      options={unidadeGestoraOptions.GerenciaGeral}
                      loading={loading[Fields.GerenciaGeral]}
                    />
                  </Grid>
                </Then>
              </If>
              <If condition={showInputs.Gerencia}>
                <Then>
                  <Grid item md={gridCols.Gerencia}>
                    <AutoCompleteSearchTerm
                      name={Fields.Gerencia}
                      formValue={Fields.Gerencia}
                      placeholder="Selecionar"
                      disabled={
                        disableFields?.includes(Fields.Gerencia) || disabled
                      }
                      value={getValue(Fields.Gerencia)}
                      onChange={(_event, value) =>
                        handleChangeUnidadeGestora(value, 'Gerencia')
                      }
                      onChangeText={useDebounce(
                        handleSearchUnidadeGestora,
                        600
                      )}
                      optionLabel="nmUnidade"
                      label="Gerência"
                      options={unidadeGestoraOptions.Gerencia}
                      loading={loading[Fields.Gerencia]}
                    />
                  </Grid>
                </Then>
              </If>
            </Else>
          </If>
        </Grid>
      </>
    )
  }
)

UnidadeGestoraFields.displayName = 'UnidadeGestoraFields'

export default UnidadeGestoraFields
